import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { useMountTransition } from '@/hooks/useMountTransition';
import { useToastStore, type Toast } from '@/store/toastStore';

/** Draws the toasts in the store (store/toastStore.ts). Mounted once, by the app. */
export function Toaster(): ReactNode {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    // Polite, not assertive: an operator mid-sentence in a form should not
    // have a screen reader interrupt them to report a background success.
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

const TONE_ICON = { success: CircleCheck, error: CircleAlert, info: Info } as const;

function ToastItem({ toast, onDismiss }: { readonly toast: Toast; readonly onDismiss: () => void }): ReactNode {
  const { phase } = useMountTransition(true, 180);
  const Icon = TONE_ICON[toast.tone];

  return (
    <div className={`toast toast-${toast.tone} popover-${phase}`}>
      <Icon size={16} aria-hidden="true" />
      <div className="toast-text">
        <div>{toast.message}</div>
        {toast.detail ? <div className="toast-detail">{toast.detail}</div> : null}
      </div>
      <button type="button" className="toast-close" aria-label="Закрыть" onClick={onDismiss}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
